import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  Shield, 
  Package, 
  Unlock,
  X,
  Check,
  Search,
  UserCog,
  Mail,
  Calendar,
  MoreVertical,
  ShieldCheck,
  ShieldOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleConfig = {
  admin: { label: "Администратор", icon: Shield, color: "bg-red-100 text-red-700", badgeColor: "bg-red-500" },
  supplier: { label: "Поставщик", icon: Package, color: "bg-blue-100 text-blue-700", badgeColor: "bg-blue-500" },
  unblocker: { label: "Разблокировщик", icon: Unlock, color: "bg-green-100 text-green-700", badgeColor: "bg-green-500" }
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "supplier",
    email: ""
  });

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        toast.error("Ошибка загрузки пользователей");
      }
    } catch (err) {
      toast.error("Ошибка подключения");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        const res = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        });
        
        if (res.ok) {
          toast.success("Пользователь обновлён");
          fetchUsers();
          closeModal();
        } else {
          const err = await res.json();
          toast.error(err.detail || "Ошибка обновления");
        }
      } else {
        const res = await fetch(`${API_URL}/api/admin/users`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (res.ok) {
          toast.success("Пользователь создан");
          fetchUsers();
          closeModal();
        } else {
          const err = await res.json();
          toast.error(err.detail || "Ошибка создания");
        }
      }
    } catch (err) {
      toast.error("Ошибка сети");
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Удалить пользователя "${username}"? Это действие нельзя отменить.`)) return;
    
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Пользователь удалён");
        fetchUsers();
      } else {
        toast.error("Ошибка удаления");
      }
    } catch (err) {
      toast.error("Ошибка сети");
    }
  };

  const handleToggleActive = async (user) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !user.is_active })
      });
      
      if (res.ok) {
        toast.success(user.is_active ? "Пользователь деактивирован" : "Пользователь активирован");
        fetchUsers();
      }
    } catch (err) {
      toast.error("Ошибка");
    }
  };

  const handleMakeAdmin = async (user) => {
    if (!window.confirm(`Назначить "${user.username}" администратором?`)) return;
    
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: "admin" })
      });
      
      if (res.ok) {
        toast.success(`${user.username} теперь администратор`);
        fetchUsers();
      }
    } catch (err) {
      toast.error("Ошибка");
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: "",
        role: user.role,
        email: user.email || ""
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: "",
        password: "",
        role: "supplier",
        email: ""
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ username: "", password: "", role: "supplier", email: "" });
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesRole = filterRole === "all" || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === "admin").length,
    suppliers: users.filter(u => u.role === "supplier").length,
    unblockers: users.filter(u => u.role === "unblocker").length,
    active: users.filter(u => u.is_active !== false).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <UserCog className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Управление пользователями</h1>
            <p className="text-sm text-slate-500">Добавление, редактирование и управление доступом</p>
          </div>
        </div>
        <Button onClick={() => openModal()} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Добавить пользователя
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">Всего</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.admins}</p>
              <p className="text-xs text-slate-500">Админы</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.suppliers}</p>
              <p className="text-xs text-slate-500">Поставщики</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Unlock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.unblockers}</p>
              <p className="text-xs text-slate-500">Разблокировщики</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.active}</p>
              <p className="text-xs text-slate-500">Активных</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Поиск по имени или email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-users"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Все роли</option>
          <option value="admin">Администраторы</option>
          <option value="supplier">Поставщики</option>
          <option value="unblocker">Разблокировщики</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Пользователь</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Роль</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600 hidden md:table-cell">Email</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600 hidden lg:table-cell">Статус</th>
              <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600 hidden lg:table-cell">Создан</th>
              <th className="text-right px-6 py-4 text-sm font-semibold text-slate-600">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.map((user) => {
              const role = roleConfig[user.role] || roleConfig.supplier;
              const RoleIcon = role.icon;
              const isActive = user.is_active !== false;
              
              return (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${role.color}`}>
                        <span className="font-semibold text-sm">
                          {user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{user.username}</p>
                        <p className="text-xs text-slate-500 md:hidden">{user.email || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${role.color}`}>
                      <RoleIcon className="w-3.5 h-3.5" />
                      {role.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600 hidden md:table-cell">
                    {user.email ? (
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {user.email}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {isActive ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                        Активен
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                        Неактивен
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 hidden lg:table-cell">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {new Date(user.created_at).toLocaleDateString("ru-RU")}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {user.role !== "admin" && (
                        <button
                          onClick={() => handleMakeAdmin(user)}
                          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Назначить администратором"
                          data-testid={`make-admin-${user.id}`}
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(user)}
                        className={`p-2 rounded-lg ${
                          isActive 
                            ? 'text-slate-400 hover:text-orange-600 hover:bg-orange-50' 
                            : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                        }`}
                        title={isActive ? "Деактивировать" : "Активировать"}
                        data-testid={`toggle-active-${user.id}`}
                      >
                        {isActive ? <ShieldOff className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openModal(user)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Редактировать"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {user.role !== "admin" && (
                        <button
                          onClick={() => handleDelete(user.id, user.username)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Удалить"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Пользователи не найдены</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${editingUser ? 'bg-blue-100' : 'bg-green-100'}`}>
                  {editingUser ? <Edit2 className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-green-600" />}
                </div>
                <h2 className="text-lg font-semibold text-slate-800">
                  {editingUser ? "Редактировать пользователя" : "Новый пользователь"}
                </h2>
              </div>
              <button onClick={closeModal} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Имя пользователя *</label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Введите имя"
                  required
                  data-testid="input-username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Пароль {editingUser ? <span className="text-slate-400 font-normal">(оставьте пустым чтобы не менять)</span> : "*"}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Введите пароль"
                  required={!editingUser}
                  data-testid="input-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Роль</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(roleConfig).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, role: key })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.role === key 
                            ? `border-blue-500 ${config.color}` 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-xs font-medium">{config.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email (опционально)</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  data-testid="input-email"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                  Отмена
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-user">
                  {editingUser ? "Сохранить" : "Создать"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
